import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/Layout";
import { Server, Plus, Trash2, X } from "lucide-react";

export default function Nodes() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newNode, setNewNode] = useState({ name: "", ip: "", location: "Europe", load: 0 });
  const [submitting, setSubmitting] = useState(false);

  const fetchNodes = () => {
    setLoading(true);
    api.get("/nodes")
      .then((res) => {
        setNodes(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(fetchNodes, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/nodes", newNode);
      setShowModal(false);
      setNewNode({ name: "", ip: "", location: "Europe", load: 0 });
      fetchNodes();
    } catch {
      window.alert("Eklenemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sunucuyu sil?")) return;
    try {
      await api.delete(`/nodes/${id}`);
      setNodes((rows) => rows.filter((n) => n._id !== id));
    } catch {
      window.alert("Silinemedi.");
    }
  };

  return (
    <AdminLayout breadcrumb={["Operasyon", "Node & Load Balancing"]} title="Kenar Sunucular">
      <div className="p-6" data-testid="nodes-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Kenar Sunucular</h1>
          <button
            onClick={() => setShowModal(true)}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="nodes-create-btn"
          >
            <Plus size={20} /> Sunucu Ekle
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500 font-medium">Yükleniyor...</div>
        ) : nodes.length === 0 ? (
          <div className="p-8 text-center text-gray-500 font-medium">Sunucu bulunamadı.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {nodes.map((node) => {
              const statusTone =
                node.status === "online"      ? "bg-green-500/10 text-green-500" :
                node.status === "maintenance" ? "bg-sky-400/10 text-sky-400"      :
                node.status === "offline"     ? "bg-red-500/10 text-red-500"      :
                                                "bg-amber-500/10 text-amber-500";
              const load = Math.max(0, Math.min(100, Number(node.load) || 0));
              return (
                <div
                  key={node._id}
                  className="bg-slate-900/50 border border-slate-800 p-5 rounded-2xl relative group"
                  data-testid={`node-card-${node._id}`}
                >
                  <button
                    onClick={() => handleDelete(node._id)}
                    className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 text-red-500 transition-opacity"
                    title="Sil"
                    data-testid={`node-delete-${node._id}`}
                  >
                    <Trash2 size={18} />
                  </button>

                  <div className="p-3 w-fit bg-slate-950 rounded-xl mb-4">
                    <Server className="text-amber-500" />
                  </div>

                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-bold text-lg">{node.name}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-tighter ${statusTone}`}>
                      {node.status || "—"}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mb-4 font-mono">{node.ip}</p>

                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-400 font-medium">
                      <span>Yük</span>
                      <span>%{load}</span>
                    </div>
                    <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="bg-amber-500 h-full transition-all duration-1000"
                        style={{ width: `${load}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] font-mono text-gray-500 pt-1">
                      <span>Kapasite</span>
                      <span>{node.max_capacity ?? "—"}</span>
                    </div>
                    <div className="flex justify-between text-[10px] font-mono text-gray-500">
                      <span>Konum</span>
                      <span>{node.location || "—"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showModal && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            onClick={() => setShowModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
              data-testid="nodes-add-modal"
            >
              <form onSubmit={handleAdd} className="p-6 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-white">Yeni Sunucu</h3>
                  <button type="button" onClick={() => setShowModal(false)} className="text-gray-500 hover:text-white">
                    <X size={18} />
                  </button>
                </div>

                <input
                  required
                  placeholder="Sunucu Adı (Örn: Edge-TR-01)"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white focus:border-amber-500 focus:outline-none"
                  value={newNode.name}
                  onChange={(e) => setNewNode({ ...newNode, name: e.target.value })}
                  data-testid="nodes-add-name"
                />
                <input
                  required
                  placeholder="IP Adresi"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white font-mono focus:border-amber-500 focus:outline-none"
                  value={newNode.ip}
                  onChange={(e) => setNewNode({ ...newNode, ip: e.target.value })}
                  data-testid="nodes-add-ip"
                />
                <input
                  placeholder="Konum (Örn: Europe)"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white focus:border-amber-500 focus:outline-none"
                  value={newNode.location}
                  onChange={(e) => setNewNode({ ...newNode, location: e.target.value })}
                  data-testid="nodes-add-location"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-500 text-black font-bold py-3 rounded-lg hover:bg-amber-400 disabled:opacity-60"
                  data-testid="nodes-add-submit"
                >
                  {submitting ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
